<script setup lang="ts">
/**
 * "Zakaj sam svoj računovodja": public page /v2/zakaj (entry zakaj.html, V2Controller::zakajAction). Static content with a link to an
 * official source for every claim; the CSS is the design delivered as zakaj-sam-svoj-racunovodja.html and stays under the .zsr- prefix.
 */
</script>

<template>
<div class="zsr">

<!-- ================= HERO ================= -->
<header class="zsr-hero">
  <div class="zsr-wrap">
    <span class="zsr-eyebrow">Zakaj e-Podjetnik</span>
    <h1>Ali si res lahko<br>sam svoj računovodja?</h1>
    <p class="zsr-lead">
      Za večino s.p.-jev se vse skupaj zvede na tri datume v mesecu, en datum v letu
      in nekaj pravil, ki se premikajo hitreje, kot si misliš. To ni računovodstvo.
      To je koledar — in prav tam gre največkrat narobe.
    </p>
    <span class="zsr-sub">Vsaka trditev na tej strani ima povezavo na uradni vir</span>
  </div>
</header>

<!-- ================= 1. MESEČNI RITEM ================= -->
<section class="zsr-sec">
  <div class="zsr-wrap">
    <div class="zsr-sec-head">
      <span class="zsr-eyebrow">1 · Mesečni ritem</span>
      <h2>Trije datumi, ki se ponavljajo vsak mesec</h2>
      <p class="zsr-kicker">
        Ni jih treba razumeti. Treba jih je ujeti. Aplikacija, ki ti jih pokaže
        preden te ujamejo, naredi 90 % tega, za kar kličeš računovodjo.
      </p>
    </div>

    <div class="zsr-rhythm">
      <div class="zsr-track">
        <div class="zsr-track-line"></div>

        <div class="zsr-node" style="left:14%">
          <div class="zsr-dot">10.</div>
          <div class="zsr-nlabel">FURS pripravi obračun</div>
          <div class="zsr-nsub">predizpolnjen POPSV v eDavkih</div>
        </div>

        <div class="zsr-node" style="left:50%">
          <div class="zsr-dot">15.</div>
          <div class="zsr-nlabel">Popravek je tvoj</div>
          <div class="zsr-nsub">če je napačen ali ga ni, oddaš sam</div>
        </div>

        <div class="zsr-node is-pay" style="left:86%">
          <div class="zsr-dot">20.</div>
          <div class="zsr-nlabel">Prispevki plačani</div>
          <div class="zsr-nsub">za pretekli mesec</div>
        </div>
      </div>

      <div class="zsr-rhythm-note">
        <svg viewBox="0 0 20 20" fill="none" stroke="#8695a4" stroke-width="1.6" style="width:1rem;height:1rem;flex-shrink:0;margin-top:.0625rem">
          <circle cx="10" cy="10" r="7.5"/><path d="M10 6.5v.5M10 9.5v4" stroke-linecap="round"/>
        </svg>
        <span>
          Prispevki zapadejo <strong>do 20. v mesecu za pretekli mesec</strong>, predizpolnjen obračun (POPSV)
          pa FURS pripravi <strong>najpozneje do 10.</strong>; če je napačen ali ga ni, ga oddaš sam
          <strong>do 15.</strong><a class="zsr-src" href="https://spot.gov.si/sl/teme/prispevki-za-socialno-varnost" target="_blank" rel="noopener">vir 1<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4.5 2h5.5v5.5M10 2L3 9" stroke-linecap="round"/></svg></a>
          Če rok pade na nedeljo ali praznik, se premakne na naslednji delovni dan.<a class="zsr-src" href="https://mladipodjetnik.si/podjetniski-koticek/racunovodstvo/kako-podjetnik-sam-placa-prispevke-za-s-p-ali-d-o-o" target="_blank" rel="noopener">vir 2<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4.5 2h5.5v5.5M10 2L3 9" stroke-linecap="round"/></svg></a>
          Prispevki se plačujejo na <strong>tri različne račune s štirimi sklici</strong> — ena napačna referenca in plačilo se "izgubi".<a class="zsr-src" href="https://mladipodjetnik.si/podjetniski-koticek/racunovodstvo/kako-podjetnik-sam-placa-prispevke-za-s-p-ali-d-o-o" target="_blank" rel="noopener">vir 2<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4.5 2h5.5v5.5M10 2L3 9" stroke-linecap="round"/></svg></a>
        </span>
      </div>
    </div>

    <div class="zsr-annual">
      <div class="zsr-big">31. marec</div>
      <div class="zsr-kicker" style="max-width:none">
        Letni obračun (DohDej) za preteklo leto. Oddati ga je mogoče
        <strong>samo elektronsko prek eDavkov</strong> — in oddati ga moraš tudi, če nisi imel prometa.<a class="zsr-src" href="https://www.gov.si/novice/2026-03-03-obvestilo-za-podjetnike-in-podjetja-ne-pozabite-na-oddajo-obracuna-ddpo-in-dohdej-za-leto-2025/" target="_blank" rel="noopener">vir 3<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4.5 2h5.5v5.5M10 2L3 9" stroke-linecap="round"/></svg></a>
      </div>
    </div>
  </div>
</section>

<!-- ================= 2. PRAVILA SE PREMIKAJO ================= -->
<section class="zsr-sec">
  <div class="zsr-wrap">
    <div class="zsr-sec-head">
      <span class="zsr-eyebrow">2 · Premikajoča tla</span>
      <h2>Pravila se spreminjajo hitreje,<br>kot jih uspeš prebrati</h2>
      <p class="zsr-kicker">
        To ni občutek. Spodaj so samo spremembe, ki jih lahko preveriš na uradnih virih —
        vsaka s svojo povezavo. Preštej jih sam.
      </p>
    </div>

    <div class="zsr-tl">
      <div class="zsr-tl-item">
        <div class="zsr-tl-date">1. JANUAR 2024</div>
        <div class="zsr-tl-body">
          Uveden <strong>obvezni zdravstveni prispevek (OZP)</strong> — nova mesečna postavka,
          ki je nadomestila prostovoljno dopolnilno zavarovanje.<a class="zsr-src" href="https://zavezanec.zzzs.si/prispevki-za-obvezno-zdravstveno-zavarovanje/obvezni-zdravstveni-prispevek/" target="_blank" rel="noopener">vir 4<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4.5 2h5.5v5.5M10 2L3 9" stroke-linecap="round"/></svg></a>
        </div>
      </div>

      <div class="zsr-tl-item">
        <div class="zsr-tl-date">1. JANUAR 2025</div>
        <div class="zsr-tl-body">
          Prag za obvezen vstop v sistem <strong>DDV dvignjen na 60.000 €</strong> obdavčljivega prometa
          v koledarskem letu.<a class="zsr-src" href="https://mladipodjetnik.si/podjetniski-koticek/racunovodstvo/davcni-zavezanec-ali-nezavezanec" target="_blank" rel="noopener">vir 5<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4.5 2h5.5v5.5M10 2L3 9" stroke-linecap="round"/></svg></a>
        </div>
      </div>

      <div class="zsr-tl-item">
        <div class="zsr-tl-date">1. JULIJ 2025</div>
        <div class="zsr-tl-body">
          Uveden <strong>prispevek za dolgotrajno oskrbo</strong>. Za s.p., ki je hkrati zavarovanec in
          zavezanec, <strong>2 % osnove</strong>.<a class="zsr-src" href="https://www.ozs.si/novice/prispevek-za-dolgotrajno-oskrbo-685a952f2592f3337042cb80" target="_blank" rel="noopener">vir 6<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4.5 2h5.5v5.5M10 2L3 9" stroke-linecap="round"/></svg></a>
        </div>
      </div>

      <div class="zsr-tl-item is-hot">
        <div class="zsr-tl-date">1. JANUAR 2026</div>
        <div class="zsr-tl-body">
          Nov sistem za <strong>normirance (ZPZR)</strong>: namesto enotne 20-odstotne stopnje
          <strong>dvostopenjska obdavčitev 20 % / 35 %</strong> in nove meje priznanih normiranih odhodkov.<a class="zsr-src" href="https://www.ambicij.si/novice/aktualni-podatki-za-obracune-porocila-obrazci/2446-normiranci-v-2026" target="_blank" rel="noopener">vir 7<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4.5 2h5.5v5.5M10 2L3 9" stroke-linecap="round"/></svg></a>
        </div>
      </div>

      <div class="zsr-tl-item">
        <div class="zsr-tl-date">1. MAREC 2026</div>
        <div class="zsr-tl-body">
          OZP <strong>s 37,17 € na 39,36 €</strong> na mesec. Usklajuje se <strong>vsak 1. marec</strong> —
          torej se bo spremenil tudi prihodnje leto.<a class="zsr-src" href="https://zavezanec.zzzs.si/prispevki-za-obvezno-zdravstveno-zavarovanje/obvezni-zdravstveni-prispevek/" target="_blank" rel="noopener">vir 4<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4.5 2h5.5v5.5M10 2L3 9" stroke-linecap="round"/></svg></a>
        </div>
      </div>

      <div class="zsr-tl-item is-hot">
        <div class="zsr-tl-date">7. MAREC 2026 — TRI MESECE POZNEJE</div>
        <div class="zsr-tl-body">
          <strong>Novela ZPZR-A</strong> (sprejeta 26. februarja) začne veljati in pravila za normirance
          spremeni <em>znova</em>: odpravljena je <strong>petletna prepoved ponovnega vstopa</strong>,
          meje pa so zdaj <strong>120.000 / 85.000 / 50.000 €</strong> povprečnega prihodka
          dveh zaporednih let — odvisno od zavarovalnega statusa.<a class="zsr-src" href="https://www.ozs.si/novice/v-veljavi-so-omiljeni-pogoji-za-normirance-69b2b3e7d5688ae77955c137" target="_blank" rel="noopener">vir 8<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4.5 2h5.5v5.5M10 2L3 9" stroke-linecap="round"/></svg></a>
        </div>
      </div>

      <div class="zsr-tl-item">
        <div class="zsr-tl-date">1. APRIL 2026</div>
        <div class="zsr-tl-body">
          Pavšalni prispevki za <strong>popoldanski s.p. s 110,11 € na 113,01 €</strong>
          (PIZ 52,05 € + ZZ 54,53 € + DO 6,43 €). Usklajujejo se <strong>letno</strong>.<a class="zsr-src" href="https://mladipodjetnik.si/novice-in-dogodki/novice/prispevki-za-popoldanski-s-p" target="_blank" rel="noopener">vir 9<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4.5 2h5.5v5.5M10 2L3 9" stroke-linecap="round"/></svg></a>
        </div>
      </div>
    </div>

    <div class="zsr-count">
      <div class="zsr-cnum">7 sprememb v 27 mesecih</div>
      <p>
        Vse so zgoraj, vse z uradnim virom. Dve <strong>v istem letu, tri mesece narazen</strong> —
        januarja 2026 so se pravila za normirance spremenila, marca 2026 pa še enkrat.
        Zato vprašanje ni, ali znaš računovodstvo. Vprašanje je,
        <strong>kdo ti pove, da se je nekaj spremenilo, preden to ugotoviš iz kazni.</strong>
      </p>
    </div>
  </div>
</section>

<!-- ================= 3. DESET VPRAŠANJ ================= -->
<section class="zsr-sec">
  <div class="zsr-wrap">
    <div class="zsr-sec-head">
      <span class="zsr-eyebrow">3 · Kaj te v resnici skrbi</span>
      <h2>Deset vprašanj, zaradi katerih<br>kličeš računovodjo</h2>
      <p class="zsr-kicker">
        Na vsako od njih e-Podjetnik odgovori s številko in datumom — ne z razlago.
      </p>
    </div>

    <div class="zsr-qgrid">
      <div class="zsr-q"><div class="zsr-qn">1</div><p>Kdaj moram plačati prispevke — in koliko točno?</p></div>
      <div class="zsr-q"><div class="zsr-qn">2</div><p>Koliko moram vsak mesec dati na stran za davke in prispevke?</p></div>
      <div class="zsr-q"><div class="zsr-qn">3</div><p>Normiranec ali dejanski stroški — kaj se meni bolj splača?</p></div>
      <div class="zsr-q"><div class="zsr-qn">4</div><p>Kaj lahko dam v stroške in česa ne smem?</p></div>
      <div class="zsr-q"><div class="zsr-qn">5</div><p>Ali moram kaj plačati tudi v mesecu, ko nisem izdal nobenega računa?</p></div>
      <div class="zsr-q"><div class="zsr-qn">6</div><p>Koliko dohodnine bom doplačal ob letnem obračunu?</p></div>
      <div class="zsr-q"><div class="zsr-qn">7</div><p>Kako izdam pravilen račun, če nisem DDV zavezanec?</p></div>
      <div class="zsr-q"><div class="zsr-qn">8</div><p>Kaj se zgodi, če presežem prag za DDV?</p></div>
      <div class="zsr-q"><div class="zsr-qn">9</div><p>Ali lahko denar s poslovnega računa preprosto nakažem sebi?</p></div>
      <div class="zsr-q"><div class="zsr-qn">10</div><p>Ali se mi za prispevke splača urediti trajnik?</p></div>
    </div>
  </div>
</section>

<!-- ================= 4. DDV PAST ================= -->
<section class="zsr-sec">
  <div class="zsr-wrap">
    <div class="zsr-two">
      <div class="zsr-note is-amber">
        <span class="zsr-note-tag">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" style="width:.875rem;height:.875rem">
            <path d="M8 2.5 1.5 13.5h13L8 2.5Z" stroke-linejoin="round"/><path d="M8 6.5v3M8 11.3v.2" stroke-linecap="round"/>
          </svg>
          Najdražja past
        </span>
        <h3>"Nisem DDV zavezanec" ni vedno dovolj</h3>
        <p>
          ID za DDV lahko potrebuješ, <strong>še preden se približaš 60.000 €</strong>. Dovolj je, da
          prejmeš storitev od davčnega zavezanca iz druge države članice EU, da storitev opraviš
          zanj — ali da tvoji nakupi blaga v EU v koledarskem letu presežejo <strong>10.000 €</strong>.<a class="zsr-src" href="https://www.asociacija.si/si/2025/02/27/servis-atipicni_e-zavezanec_ke-za-ddv/" target="_blank" rel="noopener">vir 10<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4.5 2h5.5v5.5M10 2L3 9" stroke-linecap="round"/></svg></a>
        </p>
        <ul>
          <li>Takrat postaneš <strong>atipični davčni zavezanec</strong> — ID za DDV imaš, DDV na svoje račune pa še vedno ne obračunavaš.</li>
          <li>Z njim pride obveznost: <strong>DDV-O in RP-O do 20. v naslednjem mesecu.</strong></li>
          <li>Prav tu največ s.p.-jev ugotovi, da bi moralo biti nekaj urejeno — leto prepozno.</li>
        </ul>
      </div>

      <div class="zsr-note is-emerald">
        <span class="zsr-note-tag">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" style="width:.875rem;height:.875rem">
            <path d="M3 8.5l3.5 3.5L13 5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Kaj to pomeni zate
        </span>
        <h3>Vsak mesec veš troje</h3>
        <p>
          Najpomembnejša obljuba ni "ERP za s.p.". Je bolj preprosta, in zato bolj uporabna:
        </p>
        <ul>
          <li><strong>Kaj moraš narediti</strong> — kateri obračun, kateri obrazec, kje.</li>
          <li><strong>Koliko moraš plačati</strong> — konkreten znesek, na pravi račun, s pravim sklicem.</li>
          <li><strong>Do kdaj</strong> — in opozorilo, preden rok mine, ne potem.</li>
        </ul>
        <p style="margin-top:.875rem">
          Brez računovodskega jezika. En s.p., ne štiri Excel tabele.
        </p>
      </div>
    </div>
  </div>
</section>

<!-- ================= 5. POŠTENO ================= -->
<section class="zsr-sec">
  <div class="zsr-wrap">
    <div class="zsr-sec-head">
      <span class="zsr-eyebrow">4 · Pošteno</span>
      <h2>Kdaj brez računovodje ne bo šlo</h2>
      <p class="zsr-kicker">
        Ker če ti to zamolčimo, boš to ugotovil takrat, ko bo najdražje.
      </p>
    </div>

    <div class="zsr-honest">
      <ul>
        <li>
          <svg viewBox="0 0 20 20" fill="none" stroke="#5b6b7c" stroke-width="1.7"><circle cx="10" cy="10" r="7.5"/><path d="M7 10h6" stroke-linecap="round"/></svg>
          <span>Ko <strong>zaposliš prvo osebo</strong> — obračun plač je svoja zgodba.</span>
        </li>
        <li>
          <svg viewBox="0 0 20 20" fill="none" stroke="#5b6b7c" stroke-width="1.7"><circle cx="10" cy="10" r="7.5"/><path d="M7 10h6" stroke-linecap="round"/></svg>
          <span>Ko postaneš <strong>polni DDV zavezanec</strong> z rednimi obračuni in odbitnim deležem.</span>
        </li>
        <li>
          <svg viewBox="0 0 20 20" fill="none" stroke="#5b6b7c" stroke-width="1.7"><circle cx="10" cy="10" r="7.5"/><path d="M7 10h6" stroke-linecap="round"/></svg>
          <span>Pri <strong>zalogah, proizvodnji in uvozu</strong> iz tretjih držav.</span>
        </li>
        <li>
          <svg viewBox="0 0 20 20" fill="none" stroke="#5b6b7c" stroke-width="1.7"><circle cx="10" cy="10" r="7.5"/><path d="M7 10h6" stroke-linecap="round"/></svg>
          <span>Pri <strong>davčnem nadzoru ali davčnem dolgu</strong> — tam hočeš človeka, ne aplikacije.</span>
        </li>
        <li>
          <svg viewBox="0 0 20 20" fill="none" stroke="#5b6b7c" stroke-width="1.7"><circle cx="10" cy="10" r="7.5"/><path d="M7 10h6" stroke-linecap="round"/></svg>
          <span>Pri <strong>prehodih med statusi</strong> — iz popoldanskega v polni s.p., iz normiranca v dejanske stroške, pri zaprtju sredi leta.</span>
        </li>
      </ul>
      <p class="zsr-kicker" style="margin-top:1.25rem; padding-top:1.125rem; border-top:1px solid #e4e9ee; max-width:none">
        Za vse ostalo — za tistih enajst mesecev na leto, ko se nič posebnega ne zgodi in
        samo nekaj plačaš in izdaš nekaj računov — <strong>računovodje ne potrebuješ.</strong>
        Potrebuješ koledar, ki ti pove, kaj, koliko in do kdaj. In mnogi s.p.-ji še vedno
        lahko začnejo sami, računovodjo pa dodajo samo za letni obračun.
      </p>
    </div>
  </div>
</section>

<!-- ================= CTA ================= -->
<div class="zsr-wrap">
  <div class="zsr-cta">
    <h2>Kateri paket je tvoj?</h2>
    <p>Trije klik-odgovori in takoj vidiš svojo ceno in kaj dobiš.</p>
    <a class="zsr-btn" href="/v2/sam-svoj-racunovodja">
      Poglej cenik
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" style="width:.875rem;height:.875rem">
        <path d="M3 8h9M8.5 4.5 12 8l-3.5 3.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </a>
    <p class="zsr-cta-fine">30 dni brezplačno · odpoved z enim klikom</p>
  </div>
</div>

<!-- ================= VIRI ================= -->
<footer class="zsr-sources">
  <div class="zsr-wrap">
    <h3>Viri</h3>
    <ol>
      <li><a href="https://spot.gov.si/sl/teme/prispevki-za-socialno-varnost" target="_blank" rel="noopener">SPOT / GOV.SI — Prispevki za socialno varnost</a> (roki 10., 15., 20.)</li>
      <li><a href="https://mladipodjetnik.si/podjetniski-koticek/racunovodstvo/kako-podjetnik-sam-placa-prispevke-za-s-p-ali-d-o-o" target="_blank" rel="noopener">Mladipodjetnik.si — Plačilo prispevkov s.p.</a> (računi, sklici, prestavitev roka)</li>
      <li><a href="https://www.gov.si/novice/2026-03-03-obvestilo-za-podjetnike-in-podjetja-ne-pozabite-na-oddajo-obracuna-ddpo-in-dohdej-za-leto-2025/" target="_blank" rel="noopener">GOV.SI — Rok za oddajo obračuna DDPO in DohDej</a> (31. marec, samo eDavki)</li>
      <li><a href="https://zavezanec.zzzs.si/prispevki-za-obvezno-zdravstveno-zavarovanje/obvezni-zdravstveni-prispevek/" target="_blank" rel="noopener">ZZZS — Obvezni zdravstveni prispevek</a> (uveden 1. 1. 2024; 37,17 € → 39,36 € s 1. 3. 2026)</li>
      <li><a href="https://mladipodjetnik.si/podjetniski-koticek/racunovodstvo/davcni-zavezanec-ali-nezavezanec" target="_blank" rel="noopener">Mladipodjetnik.si — Zavezanec za DDV ali nezavezanec</a> (prag 60.000 € od 1. 1. 2025)</li>
      <li><a href="https://www.ozs.si/novice/prispevek-za-dolgotrajno-oskrbo-685a952f2592f3337042cb80" target="_blank" rel="noopener">OZS — Prispevek za dolgotrajno oskrbo</a> (od 1. 7. 2025; 2 % za s.p.)</li>
      <li><a href="https://www.ambicij.si/novice/aktualni-podatki-za-obracune-porocila-obrazci/2446-normiranci-v-2026" target="_blank" rel="noopener">Ambicij — Normiranci v 2026</a> (dvostopenjska obdavčitev, normirani odhodki)</li>
      <li><a href="https://www.ozs.si/novice/v-veljavi-so-omiljeni-pogoji-za-normirance-69b2b3e7d5688ae77955c137" target="_blank" rel="noopener">OZS — V veljavi so omiljeni pogoji za normirance</a> (ZPZR-A: 26. 2. 2026 sprejeta, 7. 3. 2026 v veljavi)</li>
      <li><a href="https://mladipodjetnik.si/novice-in-dogodki/novice/prispevki-za-popoldanski-s-p" target="_blank" rel="noopener">Mladipodjetnik.si — Prispevki za popoldanski s.p.</a> (110,11 € → 113,01 € s 1. 4. 2026)</li>
      <li><a href="https://www.asociacija.si/si/2025/02/27/servis-atipicni_e-zavezanec_ke-za-ddv/" target="_blank" rel="noopener">Asociacija — Atipični zavezanec za DDV</a> (limit 10.000 €, DDV-O in RP-O do 20.)</li>
    </ol>
    <p class="zsr-updated">
      Podatki preverjeni 24. 10. 2026. Zneski prispevkov in pragovi se spreminjajo —
      e-Podjetnik jih spremlja in preračunava v aplikaciji. Ta stran ni davčno svetovanje.
    </p>
  </div>
</footer>

</div>
</template>

<style>
/* =========================================================================
   ZAKAJ SAM SVOJ RAČUNOVODJA
   Vse je pod .zsr- predpono, da se ne tepe s Tailwindom na e-podjetnik.com.
   Barve so prevzete iz /v2/sam-svoj-racunovodja (--color-brand-blue, --color-brand-dark).
   ========================================================================= */
.zsr{
  --blue:#4b94cd;
  --blue-700:#3a7cae;
  --blue-50:#eef6fc;
  --dark:#303e4c;
  --ink:#303e4c;
  --muted:#5b6b7c;
  --faint:#8695a4;
  --line:#e4e9ee;
  --bg:#fafbfc;
  --card:#ffffff;
  --amber:#b45309;
  --amber-bg:#fffbeb;
  --amber-line:#fde68a;
  --rose:#be123c;
  --rose-bg:#fff1f2;
  --rose-line:#fecdd3;
  --emerald:#047857;
  --emerald-bg:#ecfdf5;
  --emerald-line:#a7f3d0;
  --r-lg:.5rem; --r-xl:.75rem; --r-2xl:1rem;
  --shadow:0 1px 2px rgba(48,62,76,.04), 0 4px 16px rgba(48,62,76,.05);
  --shadow-lg:0 2px 4px rgba(48,62,76,.05), 0 12px 32px rgba(48,62,76,.08);

  font-family:Inter, system-ui, sans-serif;
  color:var(--ink);
  background:var(--bg);
  -webkit-font-smoothing:antialiased;
  line-height:1.5;
}
.zsr *, .zsr *::before, .zsr *::after{box-sizing:border-box; margin:0; padding:0;}
.zsr img, .zsr svg{display:block; max-width:100%;}
.zsr a{color:inherit; text-decoration:none;}
.zsr ul{list-style:none;}

.zsr-wrap{max-width:72rem; margin-inline:auto; padding:0 1.25rem;}

/* ---------- eyebrow / headings ---------- */
.zsr-eyebrow{
  font-size:.6875rem; font-weight:700; letter-spacing:.1em; text-transform:uppercase;
  color:var(--blue); display:inline-flex; align-items:center; gap:.5rem;
}
.zsr-eyebrow::before{content:""; width:1.25rem; height:2px; background:var(--blue); border-radius:2px;}
.zsr h1{font-size:clamp(1.875rem,4.2vw,3rem); font-weight:800; letter-spacing:-.025em; line-height:1.1;}
.zsr h2{font-size:clamp(1.375rem,2.6vw,1.875rem); font-weight:800; letter-spacing:-.025em; line-height:1.2;}
.zsr h3{font-size:1rem; font-weight:700; letter-spacing:-.01em;}
.zsr .zsr-lead{font-size:clamp(1rem,1.4vw,1.1875rem); color:var(--muted); max-width:60ch; line-height:1.65;}
.zsr .zsr-kicker{font-size:.9375rem; color:var(--muted); max-width:62ch; line-height:1.65;}

/* ---------- hero ---------- */
.zsr-hero{padding:3.5rem 0 2rem;}
.zsr-hero h1{margin:.875rem 0 1rem;}
.zsr-hero .zsr-sub{
  display:inline-block; margin-top:1.5rem; padding:.5rem .875rem;
  background:var(--blue-50); border:1px solid #d3e6f5; border-radius:999px;
  font-size:.8125rem; font-weight:600; color:var(--blue-700);
}

/* ---------- section shell ---------- */
.zsr-sec{padding:2.5rem 0;}
.zsr-sec-head{margin-bottom:1.75rem;}
.zsr-sec-head h2{margin:.625rem 0 .625rem;}

/* ---------- the month rhythm strip ---------- */
.zsr-rhythm{
  background:var(--card); border:1px solid var(--line); border-radius:var(--r-2xl);
  box-shadow:var(--shadow); padding:2rem 1.75rem 1.5rem;
}
.zsr-track{position:relative; height:4.5rem; margin:2.25rem .5rem 0;}
.zsr-track-line{
  position:absolute; left:0; right:0; top:1.0625rem; height:6px; border-radius:999px;
  background:linear-gradient(90deg,#e8eef4 0%, #d6e6f3 45%, #f9d9de 100%);
}
.zsr-node{position:absolute; top:0; transform:translateX(-50%); text-align:center; width:7.5rem;}
.zsr-node .zsr-dot{
  width:2.5rem; height:2.5rem; margin:0 auto; border-radius:999px;
  display:grid; place-items:center; font-weight:800; font-size:.875rem;
  background:var(--card); border:2.5px solid var(--blue); color:var(--blue-700);
  box-shadow:0 0 0 5px var(--bg);
}
.zsr-node.is-pay .zsr-dot{border-color:#e11d48; color:var(--rose);}
.zsr-node .zsr-nlabel{margin-top:.5rem; font-size:.75rem; font-weight:700; color:var(--ink); line-height:1.3;}
.zsr-node .zsr-nsub{font-size:.6875rem; color:var(--faint); line-height:1.35; margin-top:.125rem;}
.zsr-rhythm-note{
  margin-top:1.5rem; padding-top:1rem; border-top:1px solid var(--line);
  font-size:.8125rem; color:var(--muted); display:flex; gap:.5rem; align-items:flex-start;
}

/* ---------- big annual date ---------- */
.zsr-annual{
  margin-top:1rem; display:grid; grid-template-columns:auto 1fr; gap:1.25rem; align-items:center;
  background:var(--card); border:1px solid var(--line); border-left:3px solid var(--blue);
  border-radius:var(--r-xl); padding:1.25rem 1.5rem; box-shadow:var(--shadow);
}
.zsr-annual .zsr-big{font-size:1.5rem; font-weight:800; color:var(--blue-700); letter-spacing:-.02em; white-space:nowrap;}

/* ---------- timeline ---------- */
.zsr-tl{position:relative; margin-top:.5rem; padding-left:1.75rem;}
.zsr-tl::before{
  content:""; position:absolute; left:.3125rem; top:.5rem; bottom:.5rem; width:2px;
  background:linear-gradient(180deg,var(--line) 0%, #cfdce7 50%, var(--line) 100%);
}
.zsr-tl-item{position:relative; padding:0 0 1.375rem 0;}
.zsr-tl-item::before{
  content:""; position:absolute; left:-1.625rem; top:.4375rem;
  width:.75rem; height:.75rem; border-radius:999px;
  background:var(--card); border:2.5px solid var(--blue);
}
.zsr-tl-item.is-hot::before{border-color:#e11d48;}
.zsr-tl-date{font-size:.75rem; font-weight:700; color:var(--blue-700); letter-spacing:.01em;}
.zsr-tl-item.is-hot .zsr-tl-date{color:var(--rose);}
.zsr-tl-body{font-size:.9375rem; color:var(--ink); margin-top:.1875rem; line-height:1.55;}
.zsr-tl-body strong{font-weight:700;}

/* ---------- source chip ---------- */
.zsr-src{
  display:inline-flex; align-items:center; gap:.1875rem; vertical-align:baseline;
  margin-left:.375rem; padding:.0625rem .375rem;
  font-size:.625rem; font-weight:700; letter-spacing:.02em; text-transform:uppercase;
  color:var(--faint); background:#f1f4f7; border:1px solid var(--line); border-radius:999px;
  transition:all .15s cubic-bezier(.4,0,.2,1); white-space:nowrap;
}
.zsr-src:hover{color:var(--blue-700); background:var(--blue-50); border-color:#cfe3f4;}
.zsr-src svg{width:.5625rem; height:.5625rem;}

/* ---------- count callout ---------- */
.zsr-count{
  margin-top:1.75rem; background:var(--dark); border-radius:var(--r-2xl);
  padding:1.75rem 1.875rem; color:#fff; box-shadow:var(--shadow-lg);
}
.zsr-count .zsr-cnum{
  font-size:clamp(2rem,4vw,2.75rem); font-weight:800; letter-spacing:-.03em; line-height:1;
  color:#9fcdeb;
}
.zsr-count p{margin-top:.75rem; font-size:.9375rem; line-height:1.6; color:#cfd8e1; max-width:56ch;}
.zsr-count p strong{color:#fff; font-weight:700;}

/* ---------- question grid ---------- */
.zsr-qgrid{display:grid; grid-template-columns:repeat(2,1fr); gap:.75rem;}
.zsr-q{
  background:var(--card); border:1px solid var(--line); border-radius:var(--r-xl);
  padding:1rem 1.125rem; display:grid; grid-template-columns:auto 1fr; gap:.875rem;
  align-items:start; transition:all .15s cubic-bezier(.4,0,.2,1);
}
.zsr-q:hover{border-color:#cfe3f4; box-shadow:var(--shadow); transform:translateY(-1px);}
.zsr-qn{
  width:1.5rem; height:1.5rem; border-radius:.4375rem; flex-shrink:0;
  display:grid; place-items:center; font-size:.6875rem; font-weight:800;
  background:var(--blue-50); color:var(--blue-700);
}
.zsr-q p{font-size:.875rem; font-weight:600; line-height:1.45; color:var(--ink);}

/* ---------- callout cards ---------- */
.zsr-note{
  border-radius:var(--r-2xl); padding:1.5rem 1.75rem; border:1px solid;
}
.zsr-note.is-amber{background:var(--amber-bg); border-color:var(--amber-line);}
.zsr-note.is-emerald{background:var(--emerald-bg); border-color:var(--emerald-line);}
.zsr-note-tag{
  font-size:.6875rem; font-weight:800; letter-spacing:.08em; text-transform:uppercase;
  display:inline-flex; align-items:center; gap:.375rem; margin-bottom:.625rem;
}
.zsr-note.is-amber .zsr-note-tag{color:var(--amber);}
.zsr-note.is-emerald .zsr-note-tag{color:var(--emerald);}
.zsr-note h3{margin-bottom:.625rem;}
.zsr-note p{font-size:.9375rem; line-height:1.65; color:var(--muted);}
.zsr-note ul{margin-top:.875rem; display:grid; gap:.5rem;}
.zsr-note li{
  font-size:.875rem; line-height:1.5; color:var(--ink);
  position:relative; padding-left:.875rem;
}
.zsr-note li::before{
  content:""; position:absolute; left:0; top:.4375rem;
  width:.375rem; height:.375rem; border-radius:999px;
}
.zsr-note.is-amber li::before{background:#d97706;}
.zsr-note.is-emerald li::before{background:#059669;}

.zsr-two{display:grid; grid-template-columns:1fr 1fr; gap:1rem;}

/* ---------- honest block ---------- */
.zsr-honest{
  background:var(--card); border:1px solid var(--line); border-radius:var(--r-2xl);
  padding:1.75rem 1.875rem; box-shadow:var(--shadow);
}
.zsr-honest ul{margin-top:1rem; display:grid; gap:.625rem;}
.zsr-honest li{
  font-size:.9375rem; line-height:1.55; color:var(--ink);
  display:grid; grid-template-columns:auto 1fr; gap:.625rem; align-items:start;
}
.zsr-honest li svg{width:1.125rem; height:1.125rem; margin-top:.125rem; flex-shrink:0;}

/* ---------- CTA ---------- */
.zsr-cta{
  margin:1rem 0 4rem; text-align:center;
  background:linear-gradient(135deg,#f4f9fd 0%, #eaf3fa 100%);
  border:1px solid #d9e8f4; border-radius:var(--r-2xl); padding:2.75rem 1.75rem;
}
.zsr-cta h2{margin-bottom:.75rem;}
.zsr-cta p{font-size:.9375rem; color:var(--muted); max-width:46ch; margin:0 auto 1.5rem;}
.zsr-btn{
  display:inline-flex; align-items:center; gap:.5rem; padding:.8125rem 1.5rem;
  background:var(--blue); color:#fff; font-size:.9375rem; font-weight:700;
  border-radius:var(--r-lg); box-shadow:0 1px 2px rgba(48,62,76,.1), 0 6px 18px rgba(75,148,205,.3);
  transition:all .15s cubic-bezier(.4,0,.2,1);
}
.zsr-btn:hover{background:var(--blue-700); transform:translateY(-1px);}
.zsr-cta-fine{margin-top:1rem; font-size:.75rem; color:var(--faint);}

/* ---------- sources footer ---------- */
.zsr-sources{border-top:1px solid var(--line); padding:2rem 0 3rem;}
.zsr-sources h3{font-size:.75rem; letter-spacing:.08em; text-transform:uppercase; color:var(--faint); margin-bottom:.875rem;}
.zsr-sources ol{list-style:none; display:grid; gap:.4375rem; counter-reset:s;}
.zsr-sources li{
  font-size:.75rem; color:var(--muted); line-height:1.5; counter-increment:s;
  position:relative; padding-left:1.5rem;
}
.zsr-sources li::before{
  content:counter(s) "."; position:absolute; left:0; top:0;
  color:var(--faint); font-weight:700;
}
.zsr-sources a{color:var(--blue-700); text-decoration:underline; text-decoration-color:#cfe3f4; text-underline-offset:2px;}
.zsr-sources a:hover{text-decoration-color:var(--blue);}
.zsr-updated{margin-top:1.25rem; font-size:.6875rem; color:var(--faint);}

/* ---------- responsive ---------- */
@media (max-width:860px){
  .zsr-qgrid, .zsr-two{grid-template-columns:1fr;}
  .zsr-track{height:auto; margin:1.5rem 0 0; display:grid; gap:.75rem;}
  .zsr-track-line{display:none;}
  .zsr-node{position:static; transform:none; width:auto; text-align:left;
    display:grid; grid-template-columns:auto 1fr; gap:.875rem; align-items:center;
    background:#f7f9fb; border:1px solid var(--line); border-radius:var(--r-lg); padding:.75rem .875rem;}
  .zsr-node .zsr-dot{margin:0; box-shadow:none;}
  .zsr-node .zsr-nlabel{margin-top:0;}
  .zsr-annual{grid-template-columns:1fr; gap:.5rem;}
  .zsr-rhythm{padding:1.5rem 1.25rem 1.25rem;}
  .zsr-count, .zsr-honest, .zsr-note{padding:1.375rem 1.25rem;}
}
</style>
